import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
  ButtonWithLoading,
  ResponseMESF,
  useContextMenuMESF,
} from "@dexteel/mesf-core";
import { Box, Button, Grid, Paper } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
  Column,
  GridReadyEvent,
  ColumnResizedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useTimerContext } from "../../../context/timer.provider";
import { useTorchesDataEntryActions } from "../../../hooks/useTorchesDataEntryActions";
import { Torch } from "../../../models/torches.models";
import { readTorchFromPLC } from "../../../repositories/torches.repositories";
import { UpsertTorch } from "../../modals/torch/UpsertTorch";
import { useTheme } from "@mui/material/styles";
import {
  dataEntryTableStyles,
  getTorchesDynamicHeight,
} from "../../../../../../shared/styles/dataEntryTableStyles";
import { useTorchesOptionsFunctions } from "./hooks/useTorchesOptionsFunctions";
import { useTableData } from "./hooks/useTorchesTableData";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";

export const TableTorches = () => {
  const {
    state: { deProcess },
    actions: { setNotificationMessage, setErrorMessage },
  } = useDataEntryContext();
  const [torches, setTorches] = useState<Torch[]>(deProcess?.torches || []);

  const rowCount = torches?.length || 0;
  const theme = useTheme();
  const styles = dataEntryTableStyles(theme);

  const { pause, resume } = useTimerContext();
  const { deleteTorch, refreshData, saveTorches } =
    useTorchesDataEntryActions();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [columnData, setColumnData] = useState<
    { colId: string | undefined; width: number }[]
  >([]);
  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const [selectedStrand, setSelectedStrand] = useState<any>();
  const [showModal, setShowModal] = useState<"edit" | "new" | "">("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [editingTable, setEditingTable] = useState(false);

  const readAutomaticStrands = async (
    value?: string,
    strandNumber?: number,
  ) => {
    if (value === "OK" && strandNumber !== undefined) {
      const resp = await readTorchFromPLC({
        ProcessId: deProcess!.process!.ProcessId,
        Strand: strandNumber,
      });
      if (resp.ok) {
        setNotificationMessage("Read torch from PLC successful");
        await refreshData();
        return resp as ResponseMESF;
      } else {
        setErrorMessage(resp.message);
        return {
          ok: false,
          message: "Read torch from PLC failed",
        } as ResponseMESF;
      }
    } else {
      return {} as ResponseMESF;
    }
  };

  const editStrand = (torch: Torch) => {
    pause();
    setSelectedStrand(torch);
    if (torch.HotLength != null && torch.HotLength > 0) {
      setShowModal("edit");
    }
  };

  const newStrand = (torch: Torch) => {
    pause();
    setSelectedStrand(torch);
    setShowModal("new");
  };

  const handleCellValueChanged = (
    torch: Torch,
    action: "increment" | "decrement",
    field: keyof Torch,
    value: number,
  ) => {
    const updatedTorch = torches.find(
      (iTorch: Torch) =>
        iTorch.Strand === torch.Strand && iTorch.HotLength === torch.HotLength,
    );
    if (updatedTorch) {
      setEditingTable(true);
      pause();
      let newValue = value;
      if (action === "increment") {
        newValue = ((updatedTorch[field as keyof Torch] as number) || 0) + 1;
      } else if (action === "decrement") {
        newValue = ((updatedTorch[field as keyof Torch] as number) || 0) - 1;
      }
      const totalCuts =
        field === "TotalCuts" ? newValue : updatedTorch.TotalCuts;
      const scrapped = field === "Scrapped" ? newValue : updatedTorch.Scrapped;
      const Undefined =
        field === "Undefined" ? newValue : updatedTorch.Undefined;

      const updatedTorchData = {
        ...updatedTorch,
        [field]: newValue,
        Billets: (totalCuts || 0) - (scrapped || 0) - (Undefined || 0),
      };
      setTorches((prevTorches) =>
        prevTorches.map((iTorch) => {
          if (
            iTorch.Strand === torch.Strand &&
            iTorch.HotLength === torch.HotLength
          ) {
            return updatedTorchData;
          }
          return iTorch;
        }),
      );
    }
  };

  const { columns } = useTableData({
    editStrand,
    readAutomaticStrands,
    heatStatusCode: deProcess?.process?.HeatStatusCode!,
    onCellValueChanged: handleCellValueChanged,
  });

  const totals = useMemo(
    () => ({
      cuts: torches.reduce((sum, torch) => sum + (torch.TotalCuts || 0), 0),
      unidentified: torches.reduce(
        (sum, torch) => sum + (torch.Undefined || 0),
        0,
      ),
      scrapped: torches.reduce((sum, torch) => sum + (torch.Scrapped || 0), 0),
      billets: torches.reduce((sum, torch) => sum + (torch.Billets || 0), 0),
    }),
    [torches],
  );

  const handleSaveChanges = async () => {
    setIsSubmitLoading(true);
    setEditingTable(false);
    const resp = await saveTorches(deProcess!.process!.ProcessId, torches);
    setIsSubmitLoading(false);
    resume();
    if (resp) {
      setNotificationMessage("Changes saved successfully");
    }
  };

  const handleCancelChanges = () => {
    setEditingTable(false);
    resume();
    setTorches(deProcess?.torches || []);
  };

  const handleDeleteTorch = (torch: Torch) => {
    pause();
    setSelectedStrand(torch);
    setDeleteMessage(
      `Are you sure you want to delete the torche for strand: '${torch?.Strand}' and hot length: '${torch?.HotLength}'?`,
    );
  };

  const { getMenuOptions } = useTorchesOptionsFunctions({
    newStrand,
    handleDeleteTorch,
  });

  const getContextMenuItems = (
    params: GetContextMenuItemsParams,
  ): (DefaultMenuItem | MenuItemDef)[] => {
    const data = params.node?.data;
    const nativeEvent = (
      params as GetContextMenuItemsParams & { event?: MouseEvent }
    ).event;

    if (data && nativeEvent) {
      showContextMenu(
        nativeEvent as unknown as React.MouseEvent,
        data,
        "torchesContext",
      );
      params.api.deselectAll();
      params.node?.setSelected(true);
    }
    return [];
  };

  const handleConfirmationDelete = async (value?: string) => {
    setDeleteMessage("");
    if (value !== "OK") {
      resume();
      return;
    }
    deleteTorch(selectedStrand!);
    resume();
  };

  useEffect(() => {
    setTorches(deProcess?.torches || []);
  }, [deProcess]);

  useEffect(() => {
    registerConfig({
      id: "torchesContext",
      getOptions: getMenuOptions,
    });
  }, []);

  const gridOptions = useMemo(
    () => ({
      suppressPropertyNamesCheck: true,
      enableCellChangeFlash: false,
      suppressAnimationFrame: true,
      suppressContextMenu: false,
    }),
    [],
  );

  return (
    <div onContextMenu={(e) => e.preventDefault()}>
      <Paper elevation={editingTable ? 24 : 0}>
        <Box
          padding={1}
          sx={{
            ...styles.dynamicHeight,
            height: getTorchesDynamicHeight(rowCount),
          }}
        >
          <AgGridReact
            rowData={torches}
            columnDefs={columns}
            defaultColDef={{ ...defaultColDef, sortable: false }}
            loadingOverlayComponent={CenteredLazyLoading}
            getContextMenuItems={getContextMenuItems}
            gridOptions={{ ...defaultGridOptions, ...gridOptions }}
            onRowDoubleClicked={(params) => {
              const target = params.event?.target;
              const attributes = (target as any)?.attributes;
              const value = attributes[4].value;
              const notCellCut =
                value !== "8" &&
                value !== "9" &&
                value !== "10" &&
                value !== "11";

              if (notCellCut) {
                editStrand(params.data);
              }
            }}
            onGridReady={(params: GridReadyEvent) => {
              const columns = params.api.getAllDisplayedColumns();
              const newColumnData = columns?.map((col: Column) => ({
                colId: col.getColId(),
                width: col.getActualWidth(),
              }));
              setColumnData(newColumnData || []);
            }}
            onColumnResized={(params: ColumnResizedEvent) => {
              const columns = params.api.getAllDisplayedColumns();
              const newColumnData = columns?.map((col: Column) => ({
                colId: col.getColId(),
                width: col.getActualWidth(),
              }));
              setColumnData(newColumnData || []);
            }}
          />
          <Grid sx={styles.totalsRow}>
            {columnData.map((col, index) => (
              <Grid
                key={col.colId}
                sx={styles.totalCell}
                style={{ width: `${col.width}px`, minWidth: `${col.width}px` }}
              >
                {index === 6
                  ? "Totals"
                  : col.colId === "TotalCuts"
                    ? totals.cuts
                    : col.colId === "Undefined"
                      ? totals.unidentified
                      : col.colId === "Scrapped"
                        ? totals.scrapped
                        : col.colId === "Billets"
                          ? totals.billets
                          : ""}
              </Grid>
            ))}
          </Grid>
        </Box>
        {editingTable || isSubmitLoading ? (
          <Grid item xs={12}>
            <Box sx={styles.buttonGroup} padding={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancelChanges}
                sx={styles.defaultButton}
              >
                Cancel
              </Button>

              <ButtonWithLoading
                isLoading={isSubmitLoading}
                variant="contained"
                color="primary"
                onClick={handleSaveChanges}
                sx={{
                  padding: "6px 16px",
                  fontSize: "0.875rem",
                  lineHeight: 1.75,
                  borderRadius: "4px",
                }}
              >
                <div style={{ minWidth: "100px" }}>Save</div>
              </ButtonWithLoading>
            </Box>
          </Grid>
        ) : null}
        <UpsertTorch
          show={showModal}
          onHide={() => {
            resume();
            setShowModal("");
          }}
          torch={selectedStrand}
        />
        <ConfirmationDialogRaw
          title="Confirmation"
          message={deleteMessage}
          open={deleteMessage !== ""}
          onClose={handleConfirmationDelete}
        />
      </Paper>
    </div>
  );
};
